import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemText,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import DescriptionIcon from '@mui/icons-material/Description';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WarningIcon from '@mui/icons-material/Warning';
import GavelIcon from '@mui/icons-material/Gavel';
import AssignmentIcon from '@mui/icons-material/Assignment';
import SecurityIcon from '@mui/icons-material/Security';
import axios from 'axios';
import { auth } from '../firebase';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5002';

function ContractCompare() {
  const [files, setFiles] = useState([]);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [error, setError] = useState('');

  const handleFileUpload = (event) => {
    const uploadedFiles = Array.from(event.target.files);
    const pdfFiles = uploadedFiles.filter(file => file.type === 'application/pdf');
    
    if (pdfFiles.length !== uploadedFiles.length) {
      setError('Only PDF files are allowed');
      setTimeout(() => setError(''), 3000);
    }
    
    setFiles(prevFiles => [...prevFiles, ...pdfFiles]);
  };

  const handleRemoveFile = (index) => {
    setFiles(prevFiles => prevFiles.filter((_, i) => i !== index));
  };

  const handleAnalyze = async () => {
    if (files.length === 0) {
      setError('Please upload at least one contract PDF');
      return;
    }

    setAnalyzing(true);
    setError('');
    setAnalysis(null);

    try {
      const token = await auth.currentUser.getIdToken();
      
      const formData = new FormData();
      files.forEach(file => {
        formData.append('contracts', file);
      });

      const response = await axios.post(`${API_URL}/api/analyze-contracts`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`,
        },
      });

      setAnalysis(response.data);
    } catch (err) {
      console.error('Error analyzing contracts:', err);
      setError(err.response?.data?.error || 'Failed to analyze contracts. Please try again.');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleReset = () => {
    setFiles([]);
    setAnalysis(null);
    setError('');
  };

  const renderPartyAnalysis = (party, index) => {
    const colors = ['#3b82f6', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981'];
    const color = colors[index % colors.length];

    return (
      <Card key={index} sx={{ mb: 3, borderLeft: `4px solid ${color}` }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ color, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
            <GavelIcon />
            {party.party_name || `Party ${index + 1}`}
          </Typography>
          
          {party.role && (
            <Chip label={party.role} size="small" sx={{ mb: 2 }} />
          )}

          {/* Key Points */}
          {party.key_points && party.key_points.length > 0 && (
            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1" fontWeight={600}>
                  <AssignmentIcon sx={{ fontSize: 20, verticalAlign: 'middle', mr: 1 }} />
                  Key Points ({party.key_points.length})
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <List dense>
                  {party.key_points.map((point, i) => (
                    <ListItem key={i}>
                      <ListItemText primary={`• ${point}`} />
                    </ListItem>
                  ))}
                </List>
              </AccordionDetails>
            </Accordion>
          )}

          {/* Deliverables */}
          {party.deliverables && party.deliverables.length > 0 && (
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1" fontWeight={600}>
                  <AssignmentIcon sx={{ fontSize: 20, verticalAlign: 'middle', mr: 1 }} />
                  Deliverables ({party.deliverables.length})
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <List dense>
                  {party.deliverables.map((deliverable, i) => (
                    <ListItem key={i}>
                      <ListItemText primary={`• ${deliverable}`} />
                    </ListItem>
                  ))}
                </List>
              </AccordionDetails>
            </Accordion>
          )}

          {/* Liabilities */}
          {party.liabilities && party.liabilities.length > 0 && (
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1" fontWeight={600}>
                  <WarningIcon sx={{ fontSize: 20, verticalAlign: 'middle', mr: 1 }} />
                  Liabilities ({party.liabilities.length})
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <List dense>
                  {party.liabilities.map((liability, i) => (
                    <ListItem key={i}>
                      <ListItemText primary={`• ${liability}`} />
                    </ListItem>
                  ))}
                </List>
              </AccordionDetails>
            </Accordion>
          )}

          {/* Indemnity */}
          {party.indemnity && party.indemnity.length > 0 && (
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1" fontWeight={600}>
                  <SecurityIcon sx={{ fontSize: 20, verticalAlign: 'middle', mr: 1 }} />
                  Indemnity Clauses ({party.indemnity.length})
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <List dense>
                  {party.indemnity.map((clause, i) => (
                    <ListItem key={i}>
                      <ListItemText primary={`• ${clause}`} />
                    </ListItem>
                  ))}
                </List>
              </AccordionDetails>
            </Accordion>
          )}

          {/* Risks */}
          {party.risks && party.risks.length > 0 && (
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1" fontWeight={600} color="error">
                  <WarningIcon sx={{ fontSize: 20, verticalAlign: 'middle', mr: 1 }} />
                  Risks ({party.risks.length})
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <List dense>
                  {party.risks.map((risk, i) => (
                    <ListItem key={i}>
                      <ListItemText 
                        primary={`• ${risk}`}
                        primaryTypographyProps={{ color: 'error.main' }}
                      />
                    </ListItem>
                  ))}
                </List>
              </AccordionDetails>
            </Accordion>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 2 }}>
          <CompareArrowsIcon fontSize="large" />
          Contract Compare
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Upload one or more contract PDFs to analyze key points, deliverables, liabilities, indemnity clauses, and risks for each party.
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Upload Section */}
      {!analysis && (
        <Paper sx={{ p: 4, mb: 3 }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <input
              accept="application/pdf"
              style={{ display: 'none' }}
              id="contract-upload"
              multiple
              type="file"
              onChange={handleFileUpload}
            />
            <label htmlFor="contract-upload">
              <Button
                variant="contained"
                component="span"
                startIcon={<CloudUploadIcon />}
                size="large"
                disabled={analyzing}
              >
                Upload Contract PDFs
              </Button>
            </label>
          </Box>

          {files.length > 0 && (
            <>
              <Divider sx={{ my: 3 }} />
              <Typography variant="h6" gutterBottom>
                Uploaded Contracts ({files.length})
              </Typography>
              <List>
                {files.map((file, index) => (
                  <ListItem
                    key={index}
                    secondaryAction={
                      <IconButton edge="end" onClick={() => handleRemoveFile(index)} disabled={analyzing}>
                        <DeleteIcon />
                      </IconButton>
                    }
                  >
                    <DescriptionIcon sx={{ mr: 2, color: 'primary.main' }} />
                    <ListItemText
                      primary={file.name}
                      secondary={`${(file.size / 1024 / 1024).toFixed(2)} MB`}
                    />
                  </ListItem>
                ))}
              </List>

              <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'center' }}>
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  onClick={handleAnalyze}
                  disabled={analyzing}
                  startIcon={analyzing ? <CircularProgress size={20} /> : <CompareArrowsIcon />}
                >
                  {analyzing ? 'Analyzing Contracts...' : 'Analyze Contracts'}
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={handleReset}
                  disabled={analyzing}
                >
                  Clear All
                </Button>
              </Box>
            </>
          )}
        </Paper>
      )}

      {/* Analysis Results */}
      {analysis && (
        <>
          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h5" fontWeight={600}>
              Analysis Results
            </Typography>
            <Button variant="outlined" onClick={handleReset}>
              New Analysis
            </Button>
          </Box>

          {/* Contract Summary */}
          {analysis.summary && (
            <Card sx={{ mb: 3, bgcolor: 'primary.light', color: 'primary.contrastText' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom fontWeight={600}>
                  Contract Summary
                </Typography>
                <Typography variant="body1">
                  {analysis.summary}
                </Typography>
              </CardContent>
            </Card>
          )}

          {/* Party Analysis */}
          {analysis.parties && analysis.parties.length > 0 ? (
            <Box>
              <Typography variant="h5" gutterBottom fontWeight={600} sx={{ mb: 3 }}>
                Party Analysis
              </Typography>
              {analysis.parties.map((party, index) => renderPartyAnalysis(party, index))}
            </Box>
          ) : (
            <Alert severity="info">
              No party-specific analysis available. The contract may need manual review.
            </Alert>
          )}

          {/* General Observations */}
          {analysis.general_observations && analysis.general_observations.length > 0 && (
            <Card sx={{ mt: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom fontWeight={600}>
                  General Observations
                </Typography>
                <List dense>
                  {analysis.general_observations.map((observation, i) => (
                    <ListItem key={i}>
                      <ListItemText primary={`• ${observation}`} />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </Box>
  );
}

export default ContractCompare;
